import { ChevronDown, HelpCircle } from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { getPartType, PART_TYPES } from "~/features/parts/utils/part-types";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Canonical part-type chip. Reuses PART_TYPES/getPartType (icon +
// label) so a part's type reads the same everywhere. `type` is the raw stored
// string (e.g. "Protector (Manager)"); getPartType normalizes aliases.
// Appearance is intentionally low-chrome (no filled background by default) —
// pick one of outline / ghost / dot.
export type PartTypeAppearance = "outline" | "ghost" | "dot";

interface TypeColor {
    /** icon + label + border color (text/border utilities) */
    accent: string;
    border: string;
    /** solid dot color for the "dot" appearance */
    dot: string;
}

const NEUTRAL: TypeColor = {
    accent: "text-slate-600",
    border: "border-slate-300",
    dot: "bg-slate-400",
};

const COLOR_BY_LABEL: Record<string, TypeColor> = {
    Protector: {
        accent: "text-brand-forest-700",
        border: "border-brand-forest-300",
        dot: "bg-brand-forest-600",
    },
    "Protector (Manager)": {
        accent: "text-brand-forest-700",
        border: "border-brand-forest-300",
        dot: "bg-brand-forest-600",
    },
    "Protector (Firefighter)": {
        accent: "text-brand-terra-700",
        border: "border-brand-terra-300",
        dot: "bg-brand-terra-600",
    },
    "Protector (Self-like)": {
        accent: "text-[#8a6d1f]",
        border: "border-[#e0cf94]",
        dot: "bg-[#b0902f]",
    },
    Exile: {
        accent: "text-[#5b4189]",
        border: "border-[#cbb8ee]",
        dot: "bg-[#7c5cc4]",
    },
    "Unattached Burden": {
        accent: "text-[#8a2f2e]",
        border: "border-[#f0b9b9]",
        dot: "bg-[#b3403f]",
    },
    Guide: {
        accent: "text-[#8a6d1f]",
        border: "border-[#e0cf94]",
        dot: "bg-[#b0902f]",
    },
    Other: NEUTRAL,
};

type PartTypeOption = (typeof PART_TYPES)[number];

function resolve(type: string | null | undefined) {
    const info = getPartType(type ?? "");
    return {
        Icon: info?.icon ?? HelpCircle,
        label: info?.label ?? (type?.trim() ? type : "Unclear"),
        color: (info && COLOR_BY_LABEL[info.label]) ?? NEUTRAL,
    };
}

function Chip({
    type,
    appearance,
    className,
    extra,
}: {
    type: string | null | undefined;
    appearance: PartTypeAppearance;
    className?: string;
    extra?: string;
}) {
    const { Icon, label, color } = resolve(type);
    const base =
        "inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium leading-none";
    if (appearance === "dot") {
        return (
            <span className={cn(base, "text-slate-700", className, extra)}>
                <span
                    className={cn("size-2 shrink-0 rounded-full", color.dot)}
                />
                {label}
            </span>
        );
    }
    if (appearance === "ghost") {
        return (
            <span className={cn(base, "text-slate-700", className, extra)}>
                <Icon className={cn("size-3.5 shrink-0", color.accent)} />
                {label}
            </span>
        );
    }
    // outline
    return (
        <span
            className={cn(
                base,
                "rounded-full border bg-transparent px-2.5 py-1",
                color.accent,
                color.border,
                className,
                extra,
            )}
        >
            <Icon className="size-3.5 shrink-0" />
            {label}
        </span>
    );
}

interface PartTypeBadgeBase {
    type: string | null | undefined;
    appearance?: PartTypeAppearance;
    className?: string;
}

interface PartTypeBadgeDisplayProps extends PartTypeBadgeBase {
    editable?: false;
}

interface PartTypeBadgeEditableProps extends PartTypeBadgeBase {
    /** Render the session-notes-style dropdown selector variant. */
    editable: true;
    /** Called with the selected option's primary value string. */
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Override the option list (e.g. for internal-feature entries). */
    options?: PartTypeOption[];
}

export type PartTypeBadgeProps =
    | PartTypeBadgeDisplayProps
    | PartTypeBadgeEditableProps;

export function PartTypeBadge(props: PartTypeBadgeProps) {
    const { type, appearance = "ghost", className } = props;

    if (!props.editable) {
        return (
            <Chip type={type} appearance={appearance} className={className} />
        );
    }

    const { onChange, disabled, options = PART_TYPES } = props;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disabled}>
                <button
                    type="button"
                    aria-label="Change part type"
                    className="inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <Chip
                        type={type}
                        appearance={appearance}
                        className={className}
                        extra="cursor-pointer"
                    />
                    <ChevronDown className="size-3.5 text-slate-400" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                {options.map((option) => (
                    <DropdownMenuItem
                        key={option.value.join(",")}
                        className={cn(
                            "flex cursor-pointer items-center gap-2",
                            option.value.some(
                                (value) =>
                                    value.toLowerCase() ===
                                    (type ?? "").toLowerCase(),
                            ) && "font-medium",
                        )}
                        onClick={() => onChange(option.value[0])}
                    >
                        <option.icon className="size-4" />
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
